
import { defineComponent, ref, onMounted, computed } from 'vue'
import LoadingButton from '@/views/components/LoadingButton.vue'
import ContentLayout from '@/views/layout/ContentLayout.vue'
import { Kamoku } from '@/repositories/fuzoku-meisai/data'
import { kaikeikijunRepositories } from '@/repositories/fuzoku-meisai/kaikei-kijun/kaikeikijunRepositories'
import { callApiWrapper } from '@/views/callApiWrapper'
import ResponseResultAlert from '@/views/components/ResponseResultAlert.vue'
import { JigyouhiUiItem } from '@/views/fuzoku-meisai/kaikei-kijun/data'
import { UiItemsLogic } from '@/views/fuzoku-meisai/uiItemsLogic'
import JigyouhiSettingItem from '@/views/fuzoku-meisai/kaikei-kijun/JigyouhiSettingItem.vue'
import { PostParam } from '@/repositories/fuzoku-meisai/kaikei-kijun/jigyouhiRepository'
import KaikeiKijunSettingTitle from '@/views/fuzoku-meisai/kaikei-kijun/KaikeiKijunSettingTitle.vue'

const repository = kaikeikijunRepositories.jigyouhi

const jigyouhiType = {
  Zairyouhi: 'Zairyouhi',
  Kyuyohi: 'Kyuyohi',
  Itakuhi: 'Itakuhi',
  Keihi: 'Keihi',
  UriageGenka: 'UriageGenka',
  Other: 'Other'
} as const

type JigyouhiType = typeof jigyouhiType[keyof typeof jigyouhiType];

export default defineComponent({
  components: {
    KaikeiKijunSettingTitle,
    JigyouhiSettingItem,
    ResponseResultAlert,
    ContentLayout,
    LoadingButton
  },
  setup () {
    let kamokuSource: Kamoku[] = []
    const loading = ref(false)
    const isSucceedRequest = ref(false)
    const requestError = ref<string | null>(null)
    const zairyouhiItems = ref<JigyouhiUiItem[]>([])
    const kyuyohiItems = ref<JigyouhiUiItem[]>([])
    const itakuhiItems = ref<JigyouhiUiItem[]>([])
    const keihiItems = ref<JigyouhiUiItem[]>([])
    const uriageGenkaItems = ref<JigyouhiUiItem[]>([])
    const otherItems = ref<JigyouhiUiItem[]>([])

    const isAllValid = computed(() => {
      const allCount = zairyouhiItems.value.length + kyuyohiItems.value.length +
        itakuhiItems.value.length + keihiItems.value.length + uriageGenkaItems.value.length + otherItems.value.length
      if (allCount === 0) {
        return true
      }
      const allValidCount = zairyouhiItems.value.filter(i => i.isValid).length + kyuyohiItems.value.filter(i => i.isValid).length +
        itakuhiItems.value.filter(i => i.isValid).length + keihiItems.value.filter(i => i.isValid).length +
        uriageGenkaItems.value.filter(i => i.isValid).length + otherItems.value.filter(i => i.isValid).length
      return allCount === allValidCount
    })

    onMounted(async () => {
      loading.value = true
      await callApiWrapper(requestError, async () => {
        await refreshUiData()
      })
      loading.value = false
    })

    const onClickRegister = async () => {
      loading.value = true
      onDismissAlert()

      await callApiWrapper(requestError, async () => {
        const postParams: PostParam = {
          zairyouhi: zairyouhiItems.value.map(i => ({ itemName: i.item.itemName, setting: i.item.setting.targets })),
          kyuyohi: kyuyohiItems.value.map(i => ({ itemName: i.item.itemName, setting: i.item.setting.targets })),
          itakuhi: itakuhiItems.value.map(i => ({ itemName: i.item.itemName, setting: i.item.setting.targets })),
          keihi: keihiItems.value.map(i => ({ itemName: i.item.itemName, setting: i.item.setting.targets })),
          uriageGenka: uriageGenkaItems.value.map(i => ({ itemName: i.item.itemName, setting: i.item.setting.targets })),
          other: otherItems.value.map(i => ({ itemName: i.item.itemName, setting: i.item.setting.targets }))
        }
        await repository.save(postParams)
        await refreshUiData()
        isSucceedRequest.value = true
      })
      loading.value = false
    }

    const onDismissAlert = () => {
      isSucceedRequest.value = false
      requestError.value = null
    }

    const addItem = (type: JigyouhiType) => {
      const targets = getUiItems(type)
      const newId = UiItemsLogic.makeId(targets)
      targets.push({
        isValid: false,
        item: {
          id: newId,
          itemName: '',
          setting: {
            sources: [...kamokuSource],
            targets: []
          }
        }
      })
    }

    const upItem = (type: JigyouhiType, item: JigyouhiUiItem) => {
      const items = getUiItems(type)
      UiItemsLogic.upUiItem(items, item)
    }

    const downItem = (type: JigyouhiType, item: JigyouhiUiItem) => {
      const items = getUiItems(type)
      UiItemsLogic.downUiItem(items, item)
    }

    const trashItem = (type: JigyouhiType, item: JigyouhiUiItem) => {
      const items = getUiItems(type)
      UiItemsLogic.removeUiItem(items, item)
    }

    const onChangeInput = (type: JigyouhiType, item: JigyouhiUiItem) => {
      const items = getUiItems(type)
      UiItemsLogic.updateUiItem(items, item)
    }

    const getUiItems = (type: JigyouhiType): JigyouhiUiItem[] => {
      switch (type) {
        case jigyouhiType.Zairyouhi:
          return zairyouhiItems.value
        case jigyouhiType.Kyuyohi:
          return kyuyohiItems.value
        case jigyouhiType.Itakuhi:
          return itakuhiItems.value
        case jigyouhiType.Keihi:
          return keihiItems.value
        case jigyouhiType.UriageGenka:
          return uriageGenkaItems.value
        case jigyouhiType.Other:
          return otherItems.value
        default:
          throw Error('unknown type')
      }
    }

    const refreshUiData = async () => {
      const data = await repository.list()
      kamokuSource = data.kamokuSources
      zairyouhiItems.value = data.zairyouhi.map(i => ({ item: i, isValid: true }))
      kyuyohiItems.value = data.kyuyohi.map(i => ({ item: i, isValid: true }))
      itakuhiItems.value = data.itakuhi.map(i => ({ item: i, isValid: true }))
      keihiItems.value = data.keihi.map(i => ({ item: i, isValid: true }))
      uriageGenkaItems.value = data.uriageGenka.map(i => ({ item: i, isValid: true }))
      otherItems.value = data.other.map(i => ({ item: i, isValid: true }))
    }

    return {
      loading,
      isAllValid,
      isSucceedRequest,
      requestError,
      zairyouhiItems,
      kyuyohiItems,
      itakuhiItems,
      keihiItems,
      uriageGenkaItems,
      otherItems,
      onClickRegister,
      onDismissAlert,
      addItem,
      upItem,
      downItem,
      trashItem,
      onChangeInput
    }
  }
})
